/**
 * @fileoverview Offline telemetry buffer — persists events to localStorage while the
 * browser is offline and replays them once connectivity returns.
 * @module web-telemetry/buffer
 *
 * Responsibilities:
 * - Hold telemetry events in memory + localStorage when Application Insights cannot send.
 * - Cap the buffer (drop oldest) so a long offline session can't exhaust storage.
 * - Expose flush() so the telemetry client can replay buffered events on reconnect.
 * Dependencies:
 * - Browser localStorage (persistence across reloads while offline).
 * Architecture:
 * - Local-first friendly: telemetry is captured offline (like inspection data) and
 *   synced to Application Insights when online, mirroring the app's offline model.
 * Notes:
 * - localStorage is best-effort; quota/serialization errors are swallowed by design so
 *   telemetry never breaks the host app.
 */

/**
 * @contract BufferedEvent
 * Purpose: A telemetry event captured while offline, awaiting replay.
 * Fields:  name, properties (string map), measurements (number map), timestamp (ISO).
 * Rules:   properties/measurements are already enriched before buffering.
 * Sync:    replayed to Application Insights on reconnect with `_buffered=true`.
 */
export interface BufferedEvent {
  name: string;
  properties: Record<string, string>;
  measurements: Record<string, number>;
  timestamp: string;
}

/**
 * @storage TelemetryBuffer
 * Type:   LocalStorage (key `firesquirrel-telemetry-buffer`) + in-memory mirror.
 * Schema: BufferedEvent[] (FIFO, capped at maxSize).
 * Sync Role: staging area for events that could not reach Application Insights.
 */
export class TelemetryBuffer {
  private buffer: BufferedEvent[] = [];
  private readonly maxSize: number;
  private readonly storageKey: string;

  constructor(storageKey = 'firesquirrel-telemetry-buffer', maxSize = 500) {
    this.storageKey = storageKey;
    this.maxSize = maxSize;
    this.loadFromStorage();
  }

  /**
   * @process add
   * Steps:   push event, trim to maxSize (keep newest), persist.
   * Inputs:  BufferedEvent.
   * Outputs: void.
   * Failure Handling: persistence errors are swallowed (see saveToStorage).
   * Idempotency: NOT REQUIRED (append semantics).
   */
  add(event: BufferedEvent): void {
    this.buffer.push(event);
    if (this.buffer.length > this.maxSize) {
      this.buffer = this.buffer.slice(-this.maxSize);
    }
    this.saveToStorage();
  }

  /**
   * @process flush
   * Steps:   snapshot current events, clear buffer + storage, return the snapshot.
   * Inputs:  none.
   * Outputs: BufferedEvent[] for the caller to replay.
   * Failure Handling: none required (in-memory swap).
   * Idempotency: REQUIRED (second call returns []).
   */
  flush(): BufferedEvent[] {
    const events = [...this.buffer];
    this.buffer = [];
    this.saveToStorage();
    return events;
  }

  get size(): number {
    return this.buffer.length;
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.buffer));
    } catch {
      /* storage full or unavailable — drop silently, telemetry must never throw */
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) this.buffer = JSON.parse(stored) as BufferedEvent[];
    } catch {
      /* corrupt payload — start fresh */
    }
  }
}
